import React, { useEffect } from "react";
import { useWatch, ArrayField } from "react-hook-form";
import { QuestionVariables } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { OList } from "../wombat-ui";
import { FormStepProps } from "../../screens/ReviewFormScreen";
import { ClockIcon, IconProps, StarIcon } from "../icons";
import { QuestionFormInput } from "./QuestionWithOptionsForm";
import { EyeIcon, EyeOffIcon, PaperClipIcon } from "@heroicons/react/outline";
import { Box, Grid, Tooltip } from "@chakra-ui/react";

type QuestionPreviewProps = Pick<
  FormStepProps,
  "control" | "errors" | "trigger"
> & {
  name: string;
  icon: React.ReactElement<IconProps>;
  focused: boolean;
  defaultValue: Partial<ArrayField<QuestionFormInput, "id">>;
};

const QuestionPreview: React.FC<QuestionPreviewProps> = ({
  control,
  errors,
  name,
  focused,
  trigger,
  icon,
  defaultValue,
}) => {
  // FIXME: :thinking:
  useEffect(() => {
    if (!focused)
      trigger(
        defaultValue?.type === "QuestionSkill"
          ? `${name}.skillId`
          : `${name}.label`
      );
  }, [name, trigger, focused]);

  const state: Partial<QuestionVariables> = useWatch({
    control,
    name,
    defaultValue,
  });
  const t = useTranslations();

  return (
    <Box
      id={`${name}.previewContainer`}
      borderLeftWidth={focused && "thick"}
      borderColor={errors ? "red.500" : "gray.400"}
      bg='white'
      px="4"
      py="3"
      rounded="base"
      shadow="md"
    >
      <Grid w="full" templateColumns="repeat(12, 1fr)" columnGap="4" rowGap="2">
        <div className="flex items-center w-full col-span-8 space-x-3">
          <span className="flex items-center justify-center">{icon}</span>
          <span className="text-sm">
            {state?.label || t("templates.questionPreviews.unlabeledQuestion")}
          </span>
        </div>
        <div className="flex items-center justify-end col-span-4 space-x-2 text-light-500">
          <Tooltip
            placement="bottom"
            label={
              (state?.visibility == "all" || state?.visibility === "referents")
                ? (t(
                    "templates.questionPreviews.referentOpinionAsked"
                  ) as string)
                : (t(
                    "templates.questionPreviews.referentOpinionNotAsked"
                  ) as string)
            }
          >
            <Box
              as="span"
              rounded="full"
              p="1.5"
              border="1px"
              borderColor="gray.400"
              textColor="gray.400"
            >
              {(state?.visibility == "all" || state?.visibility === "referents") ? (
                <EyeIcon className="w-4 h-4 " />
              ) : (
                <EyeOffIcon className="w-4 h-4 " />
              )}
            </Box>
          </Tooltip>
          {state?.timeTracked && (
            <Tooltip
              label={t("templates.questionPreviews.timeSpentAsked") as string}
              placement="bottom"
            >
              <Box
                as="span"
                rounded="full"
                p="1.5"
                border="1px"
                borderColor="gray.400"
                textColor="gray.400"
              >
                <ClockIcon className="w-4 h-4 text-current" />
              </Box>
            </Tooltip>
          )}
          {state?.documents?.length > 0 && (
            <Tooltip
              label={t("templates.questionPreviews.hasAttachments") as string}
              placement="bottom"
            >
              <Box
                as="span"
                rounded="full"
                p="1.5"
                border="1px"
                borderColor="gray.400"
                textColor="gray.400"
              >
                <PaperClipIcon className="w-4 h-4 text-current" />
              </Box>
            </Tooltip>
          )}
          {(state?.type === "QuestionSkill" ||
            state?.type === "QuestionEvaluation") &&
            state?.options?.find(({ isGoal }) => isGoal) && (
              <Tooltip
                label={t("templates.questionPreviews.goalLevelDefined", {
                  level: state?.options?.find(({ isGoal }) => isGoal)?.label,
                })}
              >
                <Box
                  as="span"
                  rounded="full"
                  p="1.5"
                  border="1px"
                  borderColor="gray.400"
                  textColor="gray.400"
                >
                  <StarIcon className="w-4 h-4" />
                </Box>
              </Tooltip>
            )}
        </div>
      </Grid>

      {state?.options && state?.options.length > 0 && (
        <OList
          itemsClassname="text-sm pl-1 pt-1"
          items={state.options.map(({ label }) => label)}
        />
      )}
    </Box>
  );
};

export default React.memo(QuestionPreview);
